import { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, X, Eye, EyeOff, Share2, Lock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { ShareButtons } from '@/components/ShareButtons';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PickTracker() {
  const [picks, setPicks] = useState([]);
  const [liveStats, setLiveStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [hiddenPicks, setHiddenPicks] = useState(() => {
    // Load hidden picks from localStorage
    const saved = localStorage.getItem('hiddenTrackerPicks');
    return saved ? JSON.parse(saved) : [];
  });
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Subscription state
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [user, setUser] = useState(null);

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        setUser(JSON.parse(storedUser));
        try {
          const response = await axios.get(`${API}/subscription/status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsSubscribed(response.data.is_active);
        } catch (error) {
          console.error('Subscription check failed:', error);
          setIsSubscribed(false);
        }
      }
      setSubscriptionLoading(false);
    };
    
    checkSubscription();
  }, []);

  useEffect(() => {
    fetchPicks();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refreshStats();
      }, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh, picks]);

  // Save hidden picks to localStorage
  useEffect(() => {
    localStorage.setItem('hiddenTrackerPicks', JSON.stringify(hiddenPicks));
  }, [hiddenPicks]);

  const fetchPicks = async () => {
    try {
      const response = await axios.get(`${API}/picks/active`);
      
      // Backend returns only ungraded picks — no date filter needed
      const activePicks = response.data;
      
      // Sort by most recent first
      activePicks.sort((a, b) => new Date(b.pick_date) - new Date(a.pick_date));
      
      setPicks(activePicks);
      
      // Fetch initial stats for all active picks
      if (activePicks.length > 0) {
        await refreshStats(activePicks);
      }
    } catch (error) {
      console.error('Failed to fetch picks:', error);
      toast.error('Failed to load picks');
    } finally {
      setLoading(false);
    }
  };

  const refreshStats = async (picksToRefresh = picks) => {
    setRefreshing(true);
    const statsPromises = picksToRefresh.map(async (pick) => {
      try {
        const response = await axios.get(`${API}/live-stats/${encodeURIComponent(pick.player_name)}`);
        const statsData = response.data;
        
        // Verify the game from ESPN matches the pick's game
        // This prevents showing stats from a previous game
        if (statsData.game && pick.game && statsData.status === 'final') {
          // Normalize game strings: "DAL @ ORL" -> ["dal", "orl"]
          const extractTeams = (gameStr) => {
            return gameStr.toLowerCase()
              .replace(/[^a-z\s]/g, ' ')
              .split(/\s+/)
              .filter(t => t.length >= 2 && t.length <= 4);
          };
          
          const espnTeams = extractTeams(statsData.game);
          const pickTeams = extractTeams(pick.game);
          
          // Both teams must match for it to be the same game
          // e.g., ESPN: ["dal", "orl"] vs Pick: ["dal", "bos"]
          // DAL matches but ORL != BOS, so not the same game
          const allTeamsMatch = espnTeams.length >= 2 && pickTeams.length >= 2 &&
            espnTeams.every(et => pickTeams.some(pt => et === pt || et.includes(pt) || pt.includes(et)));
          
          if (!allTeamsMatch) {
            // This is data from a DIFFERENT game - show as scheduled
            console.log(`Game mismatch for ${pick.player_name}: ESPN=${statsData.game} vs Pick=${pick.game}`);
            return { 
              [pick.id]: { 
                player_name: statsData.player_name || pick.player_name,
                status: 'scheduled',
                game_status: 'Scheduled',
                game: pick.game,
                points: 0,
                rebounds: 0,
                assists: 0,
                last_updated: new Date().toISOString(),
                message: `Tonight: ${pick.game}`,
                wrong_game: true
              } 
            };
          }
        }
        
        return { [pick.id]: statsData };
      } catch (error) {
        console.error(`Failed to fetch stats for ${pick.player_name}:`, error);
        return { [pick.id]: { error: 'Failed to fetch', player_name: pick.player_name } };
      }
    });

    const results = await Promise.all(statsPromises);
    const newStats = Object.assign({}, ...results);
    setLiveStats(newStats);
    setRefreshing(false);
    toast.success('Stats updated!');
  };

  const getPickStatus = (pick, stats) => {
    if (!stats || stats.error || stats.status === 'no_game_today') {
      return { status: 'pending', color: 'text-muted-foreground', bg: 'bg-muted' };
    }

    const propType = pick.prop_type.toLowerCase();
    const line = parseFloat(pick.line);
    let currentValue = 0;

    if (propType.includes('point') && propType.includes('assist')) {
      currentValue = (stats.points || 0) + (stats.assists || 0);
    } else if (propType.includes('point')) {
      currentValue = stats.points || 0;
    } else if (propType.includes('rebound')) {
      currentValue = stats.rebounds || 0;
    } else if (propType.includes('assist')) {
      currentValue = stats.assists || 0;
    }

    const isOver = pick.pick.toLowerCase().includes('over');
    
    if (isOver) {
      if (currentValue > line) {
        return { status: 'hitting', color: 'text-accent', bg: 'bg-accent/20', icon: TrendingUp };
      } else if (currentValue >= line * 0.8) {
        return { status: 'close', color: 'text-push', bg: 'bg-push/20', icon: TrendingUp };
      } else {
        return { status: 'under', color: 'text-loss', bg: 'bg-loss/20', icon: TrendingDown };
      }
    } else {
      if (currentValue < line) {
        return { status: 'hitting', color: 'text-accent', bg: 'bg-accent/20', icon: TrendingDown };
      } else if (currentValue <= line * 1.2) {
        return { status: 'close', color: 'text-push', bg: 'bg-push/20', icon: TrendingDown };
      } else {
        return { status: 'over', color: 'text-loss', bg: 'bg-loss/20', icon: TrendingUp };
      }
    }
  };

  const getCurrentValue = (pick, stats) => {
    if (!stats || stats.error) return 0;
    
    const propType = pick.prop_type.toLowerCase();
    
    // Check for PRA (Points + Rebounds + Assists) first - most specific
    if (propType.includes('point') && propType.includes('rebound') && propType.includes('assist')) {
      return (stats.points || 0) + (stats.rebounds || 0) + (stats.assists || 0);
    }
    // Check for PR (Points + Rebounds)
    else if (propType.includes('point') && propType.includes('rebound')) {
      return (stats.points || 0) + (stats.rebounds || 0);
    }
    // Check for PA (Points + Assists)
    else if (propType.includes('point') && propType.includes('assist')) {
      return (stats.points || 0) + (stats.assists || 0);
    }
    // Check for RA (Rebounds + Assists)
    else if (propType.includes('rebound') && propType.includes('assist')) {
      return (stats.rebounds || 0) + (stats.assists || 0);
    }
    // Single stat types
    else if (propType.includes('point')) {
      return stats.points || 0;
    } else if (propType.includes('rebound')) {
      return stats.rebounds || 0;
    } else if (propType.includes('assist')) {
      return stats.assists || 0;
    } else if (propType.includes('steal')) {
      return stats.steals || 0;
    } else if (propType.includes('block')) {
      return stats.blocks || 0;
    } else if (propType.includes('three') || propType.includes('3pt') || propType.includes('3-pt')) {
      return stats.threes || stats.three_pointers || 0;
    } else if (propType.includes('turnover')) {
      return stats.turnovers || 0;
    }
    return 0;
  };

  // Hide a pick from the tracker
  const hidePick = (pickId) => {
    setHiddenPicks(prev => [...prev, pickId]);
    toast.success('Pick hidden from tracker');
  };

  // Unhide all picks
  const unhideAllPicks = () => {
    setHiddenPicks([]);
    toast.success('All picks restored');
  };

  // Filter picks based on visibility settings
  const visiblePicks = picks.filter(pick => {
    // Don't show hidden picks
    if (hiddenPicks.includes(pick.id)) return false;
    // Don't show completed picks unless toggled
    if (!showCompleted && pick.outcome) return false;
    return true;
  });

  const completedPicks = picks.filter(p => p.outcome);
  const hiddenCount = hiddenPicks.filter(id => picks.some(p => p.id === id)).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen" data-testid="loading-indicator">
          <Loader2 className="animate-spin" size={40} />
        </div>
      </Layout>
    );
  }

  // TODO: Re-enable paywall when subscription system is ready
  // For now, allow everyone to access Live Tracker
  /*
  // Show subscription paywall for non-subscribers
  if (!isSubscribed) {
    ... paywall code disabled for now ...
  }
  */

  return (
    <Layout>
      <Helmet>
        <title>Live NBA Pick Tracker - Real-Time Stats | PrimeStatLab</title>
        <meta name="description" content="Track our NBA player prop picks in real-time as games happen. Live stats updates, progress tracking, and instant results. Never miss a winning pick." />
        <meta property="og:title" content="Live Pick Tracker - PrimeStatLab" />
        <meta property="og:description" content="Track our picks in real-time as NBA games happen." />
        <link rel="canonical" href="https://primestatlab.com/tracker" />
      </Helmet>
      <div className="container mx-auto px-6 md:px-12 py-12" data-testid="pick-tracker-page">
        <div className="mb-8">
          <h1 className="text-5xl md:text-6xl font-heading font-bold mb-4">LIVE PICK TRACKER</h1>
          <p className="text-lg text-muted-foreground mb-6">Active picks stay here until you grade them</p>
          
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              onClick={() => refreshStats()}
              disabled={refreshing}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="refresh-button"
            >
              {refreshing ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2" size={16} />
                  Refresh Stats
                </>
              )}
            </Button>
            
            <Button
              onClick={() => setAutoRefresh(!autoRefresh)}
              variant={autoRefresh ? "default" : "outline"}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? 'Auto-Refresh ON (30s)' : 'Enable Auto-Refresh'}
            </Button>

            {completedPicks.length > 0 && (
              <Button
                onClick={() => setShowCompleted(!showCompleted)}
                variant="outline"
                data-testid="show-completed-toggle"
              >
                {showCompleted ? <EyeOff size={16} className="mr-2" /> : <Eye size={16} className="mr-2" />}
                {showCompleted ? 'Hide Completed' : `Show Completed (${completedPicks.length})`}
              </Button>
            )}

            {/* Always show "Show All" button when picks are hidden OR when visible picks don't match total */}
            {(hiddenCount > 0 || visiblePicks.length < picks.length) && (
              <Button
                onClick={unhideAllPicks}
                variant="outline"
                className="text-muted-foreground border-yellow-500/50 hover:bg-yellow-500/20"
                data-testid="unhide-all-button"
              >
                Show All ({picks.length})
              </Button>
            )}

            {/* Also add a clear all hidden button that's always visible if there are any picks */}
            {picks.length > 0 && visiblePicks.length < picks.length && (
              <Button
                onClick={() => {
                  setHiddenPicks([]);
                  setShowCompleted(true);
                  localStorage.removeItem('hiddenTrackerPicks');
                  toast.success('All picks restored!');
                }}
                variant="default"
                size="sm"
                className="bg-green-600 hover:bg-green-700"
                data-testid="restore-all-button"
              >
                Restore All Hidden
              </Button>
            )}
          </div>

          {/* Status summary */}
          <div className="flex gap-4 text-sm text-muted-foreground">
            <span>Active picks: {picks.length}</span>
            <span>•</span>
            <span>Showing: {visiblePicks.length}</span>
            {visiblePicks.length < picks.length && (
              <>
                <span>•</span>
                <span className="text-yellow-500">Hidden: {picks.length - visiblePicks.length}</span>
              </>
            )}
            {completedPicks.length > 0 && (
              <>
                <span>•</span>
                <span className="text-accent">Completed: {completedPicks.length}</span>
              </>
            )}
          </div>
        </div>

        {visiblePicks.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {picks.length === 0 
                  ? "No active picks. Add picks in the admin dashboard!"
                  : "All picks are hidden or completed. Use the buttons above to view them."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {visiblePicks.map((pick) => {
              const stats = liveStats[pick.id];
              const status = getPickStatus(pick, stats);
              const currentValue = getCurrentValue(pick, stats);
              const line = parseFloat(pick.line);
              const progress = (currentValue / line) * 100;

              return (
                <Card key={pick.id} className={`bg-card border-border ${pick.outcome ? 'opacity-75' : ''}`} data-testid={`pick-${pick.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-xs uppercase px-2 py-1 border border-primary text-primary">
                            {pick.sport}
                          </span>
                          {pick.outcome && (
                            <span className={`font-mono text-xs uppercase px-2 py-1 ${
                              pick.outcome === 'win' ? 'bg-accent text-white' : 
                              pick.outcome === 'loss' ? 'bg-loss text-white' : 'bg-push text-black'
                            }`}>
                              {pick.outcome.toUpperCase()}
                            </span>
                          )}
                          {!pick.outcome && stats && stats.game_status && (
                            <span className={`font-mono text-xs uppercase px-2 py-1 ${
                              stats.game_status === 'live' ? 'bg-loss text-white' : 'bg-muted text-muted-foreground'
                            }`}>
                              {stats.game_status === 'live' ? '● LIVE' : stats.game_status}
                            </span>
                          )}
                        </div>
                        <CardTitle className="font-heading text-2xl mb-2">
                          {pick.player_name} - {pick.prop_type}
                        </CardTitle>
                        <div className="font-mono text-sm text-muted-foreground">
                          LINE: {pick.line} • PICK: {pick.pick}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {status.icon && !pick.outcome && (
                          <div className={`${status.bg} p-3 rounded`}>
                            <status.icon className={status.color} size={24} />
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => hidePick(pick.id)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Hide from tracker"
                        >
                          <X size={18} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-mono uppercase tracking-widest">Progress</span>
                          <span className={`text-2xl font-bold ${status.color}`}>
                            {currentValue} / {line}
                          </span>
                        </div>
                        <div className="w-full bg-surface-highlight h-3 rounded overflow-hidden">
                          <div
                            className={`h-full transition-all duration-500 ${
                              progress >= 100 ? 'bg-accent' : progress >= 80 ? 'bg-push' : 'bg-loss'
                            }`}
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground text-right">
                          {progress.toFixed(1)}% complete
                        </div>
                      </div>

                      {stats && !stats.error && (
                        <div className={`grid gap-4 pt-4 border-t border-border ${
                          // Dynamic grid columns based on prop type
                          pick.prop_type.toLowerCase().includes('rebound') && 
                          pick.prop_type.toLowerCase().includes('assist') && 
                          pick.prop_type.toLowerCase().includes('point') ? 'grid-cols-3' :
                          (pick.prop_type.toLowerCase().includes('point') && pick.prop_type.toLowerCase().includes('assist')) ||
                          (pick.prop_type.toLowerCase().includes('point') && pick.prop_type.toLowerCase().includes('rebound')) ||
                          (pick.prop_type.toLowerCase().includes('rebound') && pick.prop_type.toLowerCase().includes('assist')) ? 'grid-cols-2' :
                          'grid-cols-1'
                        }`}>
                          {/* Show Points if prop involves points */}
                          {pick.prop_type.toLowerCase().includes('point') && (
                            <div className="text-center">
                              <div className="text-2xl font-bold">{stats.points || 0}</div>
                              <div className="text-xs text-muted-foreground uppercase">Points</div>
                            </div>
                          )}
                          {/* Show Rebounds if prop involves rebounds */}
                          {pick.prop_type.toLowerCase().includes('rebound') && (
                            <div className="text-center">
                              <div className="text-2xl font-bold">{stats.rebounds || 0}</div>
                              <div className="text-xs text-muted-foreground uppercase">Rebounds</div>
                            </div>
                          )}
                          {/* Show Assists if prop involves assists */}
                          {pick.prop_type.toLowerCase().includes('assist') && (
                            <div className="text-center">
                              <div className="text-2xl font-bold">{stats.assists || 0}</div>
                              <div className="text-xs text-muted-foreground uppercase">Assists</div>
                            </div>
                          )}
                          {/* Show all stats for other prop types */}
                          {!pick.prop_type.toLowerCase().includes('point') && 
                           !pick.prop_type.toLowerCase().includes('rebound') && 
                           !pick.prop_type.toLowerCase().includes('assist') && (
                            <>
                              <div className="text-center">
                                <div className="text-2xl font-bold">{stats.points || 0}</div>
                                <div className="text-xs text-muted-foreground uppercase">Points</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold">{stats.rebounds || 0}</div>
                                <div className="text-xs text-muted-foreground uppercase">Rebounds</div>
                              </div>
                              <div className="text-center">
                                <div className="text-2xl font-bold">{stats.assists || 0}</div>
                                <div className="text-xs text-muted-foreground uppercase">Assists</div>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {stats && stats.error && (
                        <div className="text-sm text-muted-foreground text-center py-2">
                          {stats.status === 'no_game_today' ? 'No game scheduled today' : 'Stats unavailable - try refreshing'}
                        </div>
                      )}

                      {stats && stats.last_updated && (
                        <div className="text-xs text-muted-foreground text-right">
                          Last updated: {new Date(stats.last_updated).toLocaleTimeString()}
                        </div>
                      )}

                      {/* Share this pick */}
                      <div className="pt-3 border-t border-border">
                        <ShareButtons 
                          variant="compact"
                          url={`${window.location.origin}/tracker`}
                          title={`${pick.player_name} - ${pick.prop_type} ${pick.pick}`}
                          description={`Tracking ${pick.player_name} ${pick.prop_type} (Line: ${pick.line}) on PrimeStatLab`}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Share the whole tracker */}
        {visiblePicks.length > 0 && (
          <div className="mt-8 pt-8 border-t border-border">
            <ShareButtons 
              title="Today's Pick Tracker - PrimeStatLab"
              description={`Tracking ${visiblePicks.length} picks today on PrimeStatLab!`}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
